import React, { memo, useEffect, useState } from 'react';
import type { EpgProgram, LiveChannel } from '@lumos/contracts';
import { advanceNowNext } from '../pages/epg-now.js';
import { Marquee } from '../components/Marquee.js';
import { AirBar } from './ChannelLogo.js';
import { BigLogo, toneStyle } from './BigLogo.js';
import { useLogoLook } from './logo-look.js';
import {
  airedShare,
  cachedSchedule,
  clockLabel,
  leftLabel,
  loadSchedule,
  useNowNext,
  useWallClock,
} from './store.js';

/** «Далее»: сколько передач после идущей показывает столбец. */
const NEXT_COUNT = 12;

/**
 * Столбец выбранного канала — только справка, сверху вниз по важности
 * (правка владельца 05.10.2026):
 *
 *   канал — логотип, название, группа;
 *   сейчас в эфире — состояние и остаток, название передачи ОДНОЙ строкой
 *     (длинное — бегущей), полоса эфира между временем начала и конца;
 *   описание;
 *   далее — что будет после: время и название, строка на передачу.
 *
 * Идущая передача названа один раз — в блоке «сейчас»; прежние вкладки
 * «Сейчас / Далее» повторяли её второй карточкой. Кнопок действий нет
 * (решение владельца): смотреть — нажатием на плитку, избранное — звездой на
 * плитке.
 *
 * В листе на сенсорном экране (`onTapWatch`) повторное касание листа запускает
 * канал, о чём говорит тихая строка-подсказка.
 *
 * Ничего не выдумываем: ни картинок, ни жанра, ни возраста у передач в
 * источнике нет, и их здесь нет тоже.
 */
export const ProgramDetail = memo(function ProgramDetail({
  channel,
  busy,
  onTapWatch,
  onClose,
  onLeaveLeft,
}: {
  channel: LiveChannel;
  busy: boolean;
  /** Лист, открытый касанием: ещё одно касание его верха — смотреть. */
  onTapWatch?: (channel: LiveChannel) => void;
  /** Есть у листа на узком экране: там панель закрывается. */
  onClose?: () => void;
  /** «Влево» со списка «Далее» — обратно к плиткам (столбец рядом с плитками). */
  onLeaveLeft?: () => void;
}): React.JSX.Element {
  const wallClock = useWallClock();
  const schedule = useSchedule(channel.epgId);
  const brief = advanceNowNext(useNowNext(channel.epgId, wallClock), wallClock);
  // Пока полное расписание не пришло — «сейчас/далее», уже известные плитке.
  const known: readonly EpgProgram[] = schedule
    ?? [brief?.now ?? null, brief?.next ?? null].filter((entry): entry is EpgProgram => entry !== null);
  const current = known.find((entry) => entry.start <= wallClock && wallClock < entry.stop) ?? null;
  const program = current ?? known.find((entry) => entry.start > wallClock) ?? null;
  const description = program?.description?.trim() ?? '';
  // Далее — всё, что начинается после показанной сверху передачи.
  const after = program?.stop ?? wallClock;
  const upcoming = known.filter((entry) => entry.start >= after).slice(0, NEXT_COUNT);
  const look = useLogoLook(channel.logoUrl);
  const subtitle = channel.group ?? (channel.number !== null ? `№ ${channel.number}` : null);

  // Касание листа — смотреть; крестик и расписание живут своей жизнью.
  const onTap = onTapWatch === undefined
    ? undefined
    : (event: React.MouseEvent<HTMLDivElement>): void => {
        if ((event.target as Element).closest('button, .tv-next') !== null) return;
        onTapWatch(channel);
      };

  return (
    <div className={look?.tone ? 'tv-detail has-tone' : 'tv-detail'} style={toneStyle(look)}>
      <div
        className={onTap === undefined ? 'tv-detail-body' : 'tv-detail-body is-tappable'}
        key={`${channel.id}:${program?.start ?? 'none'}`}
        onClick={onTap}
      >
        <div className="tv-detail-channel">
          <BigLogo url={channel.logoUrl} name={channel.name} look={look} className="tv-detail-logo" />
          <span className="tv-detail-channel-text">
            <span className="tv-detail-channel-name">{channel.name}</span>
            {subtitle !== null && <span className="tv-detail-channel-group">{subtitle}</span>}
          </span>
          {onClose !== undefined && (
            <button type="button" className="tv-detail-close" aria-label="Закрыть" onClick={onClose}>
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>
          )}
        </div>

        <section className={current !== null ? 'tv-now is-live' : 'tv-now'}>
          {/* Состояние и остаток — одной тихой строкой над названием: слева
              «что это» (янтарём, пока идёт), справа — сколько осталось. */}
          {program !== null && (
            <p className="tv-now-state">
              <span className="tv-now-status">{current !== null ? 'Сейчас в эфире' : 'Следующая передача'}</span>
              <span className="tv-now-left">{capitalized(programWhen(program, wallClock))}</span>
            </p>
          )}
          {/* Название — одной строкой и некрупно: длинное идёт бегущей
              строкой, а не ломается на три и не толкает всё остальное вниз. */}
          <h2 className="tv-detail-title">
            <Marquee
              text={program?.title ?? (channel.epgId === null ? 'Прямой эфир' : 'Программа скоро')}
              active
            />
          </h2>
          {/* Полоса эфира — между временем начала и конца, как шкала. */}
          {program !== null && (
            <p className="tv-now-span">
              <span className="tv-now-clock">{clockLabel(program.start)}</span>
              {current !== null
                ? <AirBar share={airedShare(current, wallClock)} className="tv-detail-air" />
                : <span className="tv-now-dash" aria-hidden="true" />}
              <span className="tv-now-clock">{clockLabel(program.stop)}</span>
            </p>
          )}
        </section>

        {description !== '' && <p className="tv-detail-desc">{description}</p>}
        {onTapWatch !== undefined && (
          <p className="tv-detail-hint" aria-live="polite">
            {busy ? 'Подключение…' : 'Нажмите ещё раз, чтобы смотреть'}
          </p>
        )}
        {upcoming.length > 0 && <UpNext key={channel.id} programs={upcoming} onLeaveLeft={onLeaveLeft} />}
      </div>
    </div>
  );
});

/**
 * «Далее»: время начала и название, строка на передачу. В столбце рядом с
 * плитками список листается сам (остальное стоит на месте), и до него можно
 * дойти с клавиатуры: «вправо» из последней колонки плиток, «влево» — назад.
 */
function UpNext({
  programs,
  onLeaveLeft,
}: {
  programs: readonly EpgProgram[];
  onLeaveLeft?: (() => void) | undefined;
}): React.JSX.Element {
  const onKey = (event: React.KeyboardEvent<HTMLOListElement>): void => {
    if (event.key !== 'ArrowLeft') return;
    event.preventDefault();
    onLeaveLeft?.();
  };
  return (
    <section className="tv-next" aria-label="Далее по программе">
      <h3 className="tv-next-heading">Далее</h3>
      <ol
        className="tv-next-list"
        {...(onLeaveLeft === undefined ? {} : { tabIndex: 0, onKeyDown: onKey })}
      >
        {programs.map((entry) => (
          <li key={entry.start} className="tv-next-item">
            <span className="tv-next-time">{clockLabel(entry.start)}</span>
            <span className="tv-next-title" title={entry.title}>{entry.title}</span>
          </li>
        ))}
      </ol>
    </section>
  );
}

function capitalized(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** «осталось 25 мин» / «через 40 мин» / «завершилась». */
function programWhen(program: EpgProgram, now: number): string {
  if (program.stop <= now) return 'завершилась';
  if (program.start <= now) return leftLabel(program, now);
  const minutes = Math.round((program.start - now) / 60_000);
  if (minutes < 60) return `через ${Math.max(1, minutes)} мин`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `через ${hours} ч` : `через ${hours} ч ${rest} мин`;
}

/** Полное расписание канала — ради описаний и «Далее»; из памяти, если есть. */
function useSchedule(epgId: string | null): EpgProgram[] | null {
  const cachedClean = (id: string | null): EpgProgram[] | null => {
    const cached = id === null ? null : cachedSchedule(id);
    return cached === null ? null : cleanPrograms(cached);
  };
  const [programs, setPrograms] = useState<EpgProgram[] | null>(() => cachedClean(epgId));
  useEffect(() => {
    setPrograms(cachedClean(epgId));
    if (epgId === null) return;
    const abort = new AbortController();
    // Небольшая пауза: при быстром проходе по плиткам не спрашиваем каждый канал.
    const timer = window.setTimeout(() => {
      loadSchedule(epgId, abort.signal)
        .then((loaded) => {
          if (abort.signal.aborted) return;
          const clean = cleanPrograms(loaded);
          setPrograms(clean.length > 0 ? clean : null);
        })
        .catch(() => undefined);
    }, 180);
    return () => {
      window.clearTimeout(timer);
      abort.abort();
    };
  }, [epgId]);
  return programs;
}

/**
 * Расписание без мусора: передачи без длительности выброшены, а наехавшие
 * друг на друга (источник присылает правки сетки) обрезаны по началу
 * следующей — список «Сейчас / Далее» не должен показывать «00:19 – 00:04».
 */
export function cleanPrograms(programs: readonly EpgProgram[]): EpgProgram[] {
  const sorted = programs.filter((entry) => entry.stop > entry.start).sort((a, b) => a.start - b.start);
  const clean: EpgProgram[] = [];
  for (const entry of sorted) {
    const last = clean[clean.length - 1];
    if (last !== undefined && entry.start < last.stop) {
      if (entry.start === last.start) clean.pop();
      else clean[clean.length - 1] = { ...last, stop: entry.start };
    }
    clean.push(entry);
  }
  return clean;
}
